import type { ChangeRxChangePatientRequestT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { useChangeRxChangePatientMutation } from '@/entities/rx';
import { FormField } from '@/shared/ui/primitives';

import { changePatientDraftSchema, type ChangePatientDraft } from '../model/schemas';
import { useDetailAction } from '../model/useDetailAction';
import { DetailActionForm } from './DetailActionForm';

export interface RxChangeChangePatientFormProps {
  readonly rxChangeId: number;
  /** Called after a successful patient change (e.g. to close the parent sheet). */
  readonly onCompleted?: () => void;
}

const DEFAULT_VALUES: ChangePatientDraft = {
  patientUserId: '',
};

/**
 * Re-route an RxChange to a different patient when DoseSpot routed it to the
 * wrong record. ``patientUserId`` is the lotushealth user UUID for the
 * intended patient. Mutation success invalidates
 * ``rxKeys.pendingRxChangesDetailed()`` (prefix match).
 */
export function RxChangeChangePatientForm({
  rxChangeId,
  onCompleted,
}: RxChangeChangePatientFormProps) {
  const mutation = useChangeRxChangePatientMutation();
  const { form, handleSubmit } = useDetailAction({
    schema: changePatientDraftSchema,
    defaultValues: DEFAULT_VALUES,
    mutation,
    buildVariables: (draft: ChangePatientDraft) => ({
      rxChangeId,
      data: {
        patientUserId: draft.patientUserId.trim(),
      } satisfies ChangeRxChangePatientRequestT,
    }),
    successMessage: 'Patient updated for this RxChange.',
    errorTitle: 'Could not change patient.',
    logKey: 'change RxChange patient',
    logContext: { rxChangeId },
    onCompleted,
  });

  return (
    <DetailActionForm
      mutation={mutation}
      onSubmit={handleSubmit}
      submitLabel="Change patient"
      pendingLabel="Updating…"
    >
      <FormField
        form={form}
        type="text"
        name="patientUserId"
        label="Patient user ID"
        description="lotushealth user UUID of the correct patient."
        placeholder="00000000-0000-0000-0000-000000000000"
        inputProps={{ autoComplete: 'off', spellCheck: false }}
      />
    </DetailActionForm>
  );
}
